import fetchIntercept, { FetchInterceptorResponse } from 'fetch-intercept';
import {
  proxy,
  XhrRequestConfig,
  XhrRequestHandler,
  XhrError,
  XhrErrorHandler,
  XhrResponse,
  XhrResponseHandler
} from './ajax-hook';
import { IReqResponseCode, IReqResponseMode, DOUYIN_APPID } from './config';
import { IResponseHeaders, IRequestType, IEncryptApi } from '../types';
import JsonParser from './jsonParser';

type IRequestConfig = XhrRequestConfig;

export default class RequestInterceptor {
  // 业务appid
  private aid: number;
  // ZDP加解密SDK
  private secureSDK;
  // 服务端证书序列号
  private sn: string;
  // 客户端证书
  private clientCert: string;
  // 拦截url集合
  private interceptUrls: IEncryptApi[];

  constructor() {
    this.aid = this.getAppID();
    this.loadSecureSDKScript();
    this.interceptUrls = [];
  }

  // 获取业务appid
  getAppID() {
    const aidParam = document.currentScript?.getAttribute('aid');
    const aid = aidParam ? parseInt(aidParam, 10) : DOUYIN_APPID;
    return aid;
  }

  // 加载zdp sdk脚本
  loadSecureSDKScript() {
    if ((window as any).UCSecuritySDK) {
      this.initZDP();
      return;
    }
    const head = document.getElementsByTagName('HEAD').item(0);
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://unpkg.byted-static.com/byted/uc-secure-sdk/2.1.0-alpha.16/dist/index.umd.production.js';
    script.onload = () => {
      // 脚本加载完成，初始化zdp
      this.initZDP();
    };
    script.onerror = err => {
      console.log('SecureSDK脚本加载失败：', err);
    };
    head?.appendChild(script);
  }

  // 初始化ZDP SDK
  async initZDP() {
    this.secureSDK = new (window as any).UCSecuritySDK({
      proxy: false, // 由于SDK目前支持劫持能力，所有初始化的时候必须关闭劫持能力
      aid: this.aid, // aid由业务透传，默认取抖音aid
      cryptType: 'delta',
      certType: 'request',
    });
    // 初始化完成之后获取当前的端上数据
    const res = await this.secureSDK.cryptoSDK.getClientAndServerData();
    const { sn, clientCert } = res || {};
    this.sn = sn;
    this.clientCert = clientCert;
    // 初始化完成之后获取待拦截的url集合
    const response = await fetch(
      'https://dpt.bytedance.net/ies/privacy/transport_control/downstream/conf/client/get_enc_api',
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    const body = await response.json();
    this.interceptUrls = body.data?.encrypt_api_list ?? [];
    // 拦截请求
    this.requestInterceptor();
  }

  // 拦截fetch请求
  fetchInterceptor() {
    const that = this;
    fetchIntercept.register({
      request(url: any, config) {
        const request = that.interceptUrls.find(ele => url?.url?.includes(ele?.api));
        if (request && request?.method?.includes(url?.method)) {
          const newConfig = that.modifyRequestHeaders(IRequestType.Fetch, config);
          return [url, newConfig];
        }
        return [url, config];
      },

      requestError(error) {
        console.log('fetch request error:', error);
        return Promise.reject(error);
      },

      response(response: FetchInterceptorResponse) {
        const clonedResponse = response.clone();
        // 获取headers
        const headers = {};
        for (const pair of clonedResponse.headers.entries()) {
          headers[pair[0]] = pair[1];
        }
        const { code, mode, field } = that.resolveResponseHeader(headers);

        switch (code) {
          // 直接明文返回
          case IReqResponseCode.DecodeError:
          case IReqResponseCode.HeaderParamsMiss:
          case IReqResponseCode.NoEncryption:
            return response;
          // 正常加密，分字段解密和body解密进行处理
          case IReqResponseCode.Encryption:
            try {
              const json = () =>
                clonedResponse.json().then(async data => {
                  const res = JSON.stringify(data);
                  if (mode === IReqResponseMode.BodyEncrypt) {
                    return await that.secureSDK.cryptoSDK.decrypt(res);
                  }
                  return await that.decryptResponseField(field, res);
                });
              response.json = json;
              return response;
            } catch (err) {
              console.log('sdk解密失败，直接返回原始请求：', err);
              return response;
            }
          // 客户端证书错误，刷新数据返回错误
          case IReqResponseCode.ClientError:
            that.refreshData();
            that.refreshServerData();
            that.initZDP();
            return response;
          // 服务端错误，返回错误
          case IReqResponseCode.ServerError:
            return response;
          default:
            break;
        }
        return response;
      },

      responseError(error) {
        console.log('fetch response error:', error);
        return Promise.reject(error);
      },
    });
  }

  // 拦截xhr请求
  xhrInterceptor() {
    const that = this;
    proxy({
      // 请求发起前进入
      onRequest: (config: XhrRequestConfig, handler: XhrRequestHandler) => {
        const request = that.interceptUrls.find(ele => config?.url?.includes(ele?.api));
        if (request && request?.method?.includes(config.method)) {
          const newConfig = that.modifyRequestHeaders(IRequestType.XHR, config);
          handler.next(newConfig);
        } else {
          handler.next(config);
        }
      },
      // 请求发生错误时进入，比如超时；注意，不包括http状态码错误，如404仍然会认为请求成功
      onError: (err: XhrError, handler: XhrErrorHandler) => {
        console.log('xhr request error:', err);
        handler.next(err);
      },
      // 请求成功后进入
      onResponse: async (response: XhrResponse, handler: XhrResponseHandler) => {
        // 获取response header
        const responseHeaders = response.headers;
        const { code, mode, field } = that.resolveResponseHeader(responseHeaders);

        switch (code) {
          // 直接明文返回
          case IReqResponseCode.DecodeError:
          case IReqResponseCode.HeaderParamsMiss:
          case IReqResponseCode.NoEncryption:
            handler.next(response);
            break;
          // 正常加密，分字段解密和body解密进行处理
          case IReqResponseCode.Encryption:
            that.handleEncryptionResponse(mode, field, response, handler);
            break;
          // 客户端证书错误，刷新数据返回错误
          case IReqResponseCode.ClientError:
            await that.refreshData();
            await that.refreshServerData();
            await that.initZDP();
            handler.next(response);
            break;
          // 服务端错误，返回错误
          case IReqResponseCode.ServerError:
            handler.next(response);
          default:
            break;
        }
      },
    });
  }

  // 刷新端上所有数据
  async refreshData() {
    await this.secureSDK.cryptoSDK.refresh();
  }

  // 刷新当前服务端数据
  async refreshServerData() {
    await this.secureSDK.cryptoSDK.refreshServerData();
  }

  // 修改请求头
  modifyRequestHeaders(requestType: IRequestType, config: IRequestConfig): IRequestConfig {
    const cert = this.clientCert;
    const timestamp = new Date().getTime().toString();
    const sn = this.sn;
    if (requestType === IRequestType.XHR) {
      const headers = config.headers || {};
      config.headers = {
        ...headers,
        // 客户端证书信息（等同于公钥）
        'bd-timon-client-crt': cert,
        // 时间戳
        'bd-timon-ts': timestamp,
        // 服务端证书序列号
        'bd-timon-local-server-crt-sn': sn,
      };
    } else {
      config = config || ({} as IRequestConfig);
      const modifiedHeaders = new Headers(config?.headers || {});
      modifiedHeaders.append('bd-timon-client-crt', cert);
      modifiedHeaders.append('bd-timon-ts', timestamp);
      modifiedHeaders.append('bd-timon-local-server-crt-sn', sn);
      config.headers = modifiedHeaders;
    }
    return config;
  }

  // 解析响应头，获取解密信息
  resolveResponseHeader(headers: XhrResponse['headers']): IResponseHeaders {
    try {
      const code = headers['bd-timon-code'];
      const mode = headers['bd-timon-encrypt-mode'];
      const field = headers['bd-timon-encrypt-field'];
      return {
        code: code ? parseInt(code[0], 10) : IReqResponseCode.HeaderParamsMiss,
        mode: mode ? parseInt(mode, 10) : IReqResponseCode.HeaderParamsMiss,
        field: field ? JSON.parse(field) : [],
      };
    } catch (err) {
      return {
        code: IReqResponseCode.DecodeError,
      };
    }
  }

  // 处理响应解密
  async handleEncryptionResponse(
    mode: IReqResponseMode,
    field: string,
    response: XhrResponse,
    handler: XhrResponseHandler
  ) {
    // 解析body
    const responseBody = response.response;
    // body解密
    if (mode === IReqResponseMode.BodyEncrypt) {
      const res = this.secureSDK.cryptoSDK.decrypt(responseBody);
      res.then(data => {
        // 返回解密后的res
        handler.next(
          Object.assign({}, response, {
            response: data,
          })
        );
      });
    } else {
      // 字段解密
      const res = await this.decryptResponseField(field, responseBody);
      handler.next(
        Object.assign({}, response, {
          response: res,
        })
      );
    }
  }

  // 解密响应字段
  async decryptResponseField(field: string, responseBody: XhrResponse['response']): Promise<XhrResponse['response']> {
    try {
      const parser = new JsonParser(JSON.parse(responseBody));

      // 循环处理field每一个加密字段
      for (const path of field) {
        await parser.modifyNodeByPath(path, (val: string) => {
          return new Promise(async resolve => {
            const res = await this.secureSDK.cryptoSDK.decrypt(val);
            resolve(res);
          });
        });
      }
      return JSON.stringify(parser.nodeVal);
    } catch (err) {
      return null;
    }
  }

  // 开启请求劫持
  requestInterceptor() {
    this.fetchInterceptor();
    this.xhrInterceptor();
  }
}
